import React from "react";
import { Cube, Clock, Stack, Receipt, Coins, Hash, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { CopyBtn } from "@/components/primitives";
import { formatLovelace, truncMid, unixToDate } from "@/lib/format";

// ---------- Isometric cube graphic (block visual) ----------
const _cubePolys = (cx, cy, a, v) => {
  const T = [cx, cy], R = [cx + a, cy + a / 2], B = [cx, cy + a], L = [cx - a, cy + a / 2];
  const Bv = [cx, cy + a + v], Lv = [cx - a, cy + a / 2 + v], Rv = [cx + a, cy + a / 2 + v];
  const mid = (p, q) => [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2];
  const pts = (arr) => arr.map((p) => p.join(",")).join(" ");
  return {
    top: pts([T, R, B, L]),
    left: pts([L, B, Bv, Lv]),
    right: pts([R, B, Bv, Rv]),
    c1: [mid(T, L), mid(R, B)],
    c2: [mid(T, R), mid(L, B)],
  };
};

const IsoCube = ({ cx, cy, a, v, opacity = 1 }) => {
  const p = _cubePolys(cx, cy, a, v);
  return (
    <g opacity={opacity} style={{ filter: "drop-shadow(0 0 6px rgba(157,92,255,0.45))" }}>
      <polygon points={p.top} fill="hsl(264 100% 68% / 0.14)" stroke="hsl(264 100% 68%)" strokeWidth="1.5" strokeLinejoin="round" />
      <polygon points={p.left} fill="hsl(264 100% 68% / 0.05)" stroke="hsl(264 100% 68% / 0.7)" strokeWidth="1.5" strokeLinejoin="round" />
      <polygon points={p.right} fill="hsl(264 100% 68% / 0.09)" stroke="hsl(264 100% 68% / 0.7)" strokeWidth="1.5" strokeLinejoin="round" />
      <line x1={p.c1[0][0]} y1={p.c1[0][1]} x2={p.c1[1][0]} y2={p.c1[1][1]} stroke="hsl(264 100% 68% / 0.45)" strokeWidth="1" />
      <line x1={p.c2[0][0]} y1={p.c2[0][1]} x2={p.c2[1][0]} y2={p.c2[1][1]} stroke="hsl(264 100% 68% / 0.45)" strokeWidth="1" />
    </g>
  );
};

const BlockMetricRow = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="flex items-start gap-3 py-3 border-b border-primary/10 last:border-b-0" data-testid={testid}>
    <div className="w-8 h-8 shrink-0 rounded-lg border border-primary/25 bg-primary/[0.06] flex items-center justify-center mt-0.5">
      <Icon size={15} className="text-primary" weight="bold" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">{label}</div>
      <div className="font-mono text-[14px] tabnum text-foreground mt-0.5 break-all">{value}</div>
      {sub ? <div className="text-[11px] text-muted-foreground/70 mono mt-0.5">{sub}</div> : null}
    </div>
  </div>
);

// ---------- Blocks mode summary panel ----------
export const BlockSummaryPanel = ({ data, amountMode, onNavigate }) => {
  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="block-summary-panel">
      <div className="relative overflow-hidden rounded-2xl border border-primary/25 bg-card/30 backdrop-blur-md box-glow">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-70 tracing-beam pointer-events-none" />
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] divide-y lg:divide-y-0 lg:divide-x divide-primary/15">
          {/* LEFT — visual */}
          <div className="p-6 md:p-8">
            <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mono mb-3">Block</div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-heading text-[32px] md:text-[42px] leading-none text-primary text-glow">#{data.height}</span>
              <CopyBtn value={String(data.height)} label="height" />
            </div>
            {data.hash ? (
              <div className="font-mono text-[12px] text-muted-foreground/70 break-all mt-3">{data.hash}</div>
            ) : null}
            <div className="mt-6 flex items-center justify-center">
              <svg viewBox="0 0 360 290" className="w-full max-w-[360px]" data-testid="block-cube-graphic">
                <IsoCube cx={180} cy={150} a={90} v={45} opacity={0.45} />
                <IsoCube cx={108} cy={120} a={62} v={34} opacity={0.85} />
                <IsoCube cx={252} cy={132} a={62} v={34} opacity={0.85} />
                <IsoCube cx={180} cy={48} a={78} v={66} opacity={1} />
              </svg>
            </div>
          </div>
          {/* RIGHT — data */}
          <div className="p-6 md:p-8">
            <BlockMetricRow icon={Cube} label="Height" testid="block-row-height"
              value={data.height ?? "—"} sub={`${data.confirmations ?? 0} conf`} />
            <BlockMetricRow icon={Clock} label="Time" testid="block-row-time"
              value={unixToDate(data.time)} sub={`slot ${data.slot ?? "—"}`} />
            <BlockMetricRow icon={Stack} label="Epoch" testid="block-row-epoch"
              value={data.epoch ?? "—"} sub={`epoch slot ${data.epoch_slot ?? "—"}`} />
            <BlockMetricRow icon={Receipt} label="Tx Count" testid="block-row-txcount"
              value={data.tx_count ?? 0} sub={`size ${(data.size ?? 0).toLocaleString("en-US")} B`} />
            <BlockMetricRow icon={Coins} label="Total Fees" testid="block-row-fees"
              value={formatLovelace(data.fees, amountMode)} />
            <BlockMetricRow icon={Coins} label="Total Output" testid="block-row-output"
              value={formatLovelace(data.output, amountMode)} />
            <BlockMetricRow icon={Cube} label="Slot Leader" testid="block-row-leader"
              value={data.slot_leader ? (
                <span className="inline-flex items-center gap-2">
                  {truncMid(data.slot_leader, 12, 8)}
                  <CopyBtn value={data.slot_leader} label="leader" />
                </span>
              ) : "—"} />
            <BlockMetricRow icon={Hash} label="Hash" testid="block-row-hash"
              value={data.hash ? (
                <span className="inline-flex items-center gap-2">
                  {truncMid(data.hash, 12, 6)}
                  <CopyBtn value={data.hash} label="hash" />
                </span>
              ) : "—"} />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          data-testid="block-nav-previous"
          onClick={() => data.previous_block && onNavigate?.(data.previous_block)}
          disabled={!data.previous_block}
          className="inline-flex items-center gap-2 border border-border/70 bg-card px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] hover:bg-muted/30 disabled:opacity-40 transition-colors"
        >
          <CaretLeft size={12} /> previous block
        </button>
        <button
          data-testid="block-nav-next"
          onClick={() => data.next_block && onNavigate?.(data.next_block)}
          disabled={!data.next_block}
          className="inline-flex items-center gap-2 border border-border/70 bg-card px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] hover:bg-muted/30 disabled:opacity-40 transition-colors"
        >
          next block <CaretRight size={12} />
        </button>
      </div>
    </div>
  );
};
